import React, { useMemo, useRef, useEffect, useContext, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FiSearch, FiHeart, FiSettings, FiShoppingBag, FiShoppingCart, FiMenu, FiRss, FiUser, FiBell, FiMessageCircle } from "react-icons/fi";
import LanguageSelector from "../../components/common/LanguageSelector";
import NotificationBell from "../../components/notifications/NotificationBell";
import useInboxUnreadCount from "../../hooks/useInboxUnreadCount";
import { Outlet, NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import { Suspense } from "react";
import PageSkeleton from "../../components/ui/PageSkeleton";
import {
  getDineInTableQrContext,
  isDineInTableQrSeatSetupComplete,
  buildRestaurantMenuUrlWithTableQr,
  buildDiningTableSessionUrl,
  TABLE_QR_SESSION_ENDED_EVENT,
} from "../../utils/dineInTableQrSession";
import { toast } from "sonner";
import { CART_CHANGED_EVENT } from "../../utils/cartEvents";
import { getGuestCartItemCount } from "../../utils/guestCartStorage";
import { AuthContext } from "../../contexts/AuthContext";
import api from "../../api/client";
import AppFooter from "../../components/layout/AppFooter";
import StudentProfilePromptModal from "../auth/user/StudentProfilePromptModal";
import PhoneNumberPromptModal from "../auth/user/PhoneNumberPromptModal";
import StudentStatusBanner from "../auth/user/StudentStatusBanner";
import SiteBrandMark from "../../components/common/SiteBrandMark";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import {
  SHARE_PROMPT_EVENT,
  clearScheduledSharePrompt,
  readScheduledSharePrompt,
} from "../../utils/postPickupSharePromptClient";
import { sharePromptRewardMessage } from "../../utils/sharePromptRewardMessage";
import {
  getRestaurantIdFromMenuPath,
  hasMenuScrollRestore,
} from "../../utils/menuScrollRestore";
import { userProfilePath } from "../../utils/tasteSelections";
import { prefetchFavoritesPageData } from "../../utils/favoritesPageCache";
import { prefetchFeedPageData, readFeedCoords, readFeedSort } from "../../utils/feedPageCache";

const LS_LAST_CART = "leMemePrix_lastCartRestaurantId";
const LS_SIDEBAR_EXPANDED = "user-sidebar-expanded";

const mobileTabClass = (active) =>
  `flex flex-col items-center justify-center flex-1 min-w-0 py-2 transition-colors ${
    active ? "text-brand" : "text-slate-500"
  }`;

const navLinkClass = (isActive, expanded) =>
  expanded
    ? isActive
      ? "user-nav-item-active"
      : "user-nav-item hover:bg-slate-50 hover:text-slate-900"
    : `flex items-center justify-center rounded-xl py-2.5 mx-1.5 transition-colors ${
        isActive ? "bg-brand/15 text-brand font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
      }`;

const sidebarMenuBtnClass =
  "shrink-0 rounded-lg p-2 text-slate-700 transition-colors hover:bg-brand/10 hover:text-brand";

const UserLayout = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { siteName, feedShareRewards } = useSiteSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const mainScrollRef = useRef(null);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [tableQrStripTick, setTableQrStripTick] = useState(0);
  const { unreadCount: inboxUnreadCount, chatUnreadCount } = useInboxUnreadCount("user", !!user);
  const bellUnreadCount = Math.max(0, inboxUnreadCount - chatUnreadCount);

  useEffect(() => {
    const onEnded = () => setTableQrStripTick((n) => n + 1);
    window.addEventListener(TABLE_QR_SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(TABLE_QR_SESSION_ENDED_EVENT, onEnded);
  }, []);

  useEffect(() => {
    const userId = user?.id || user?._id || user?.userId;
    let cancelled = false;
    const warmPages = () => {
      if (cancelled) return;
      import("../menu/user/TastesFeedPage").catch(() => {});
      prefetchFeedPageData(api, {
        userId,
        sort: readFeedSort(),
        coords: readFeedCoords(),
      }).catch(() => {});
      if (!userId) return;
      import("../menu/user/FavoritesPage").catch(() => {});
      prefetchFavoritesPageData(api, userId).catch(() => {});
    };
    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(warmPages, { timeout: 2000 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }
    const timer = setTimeout(warmPages, 800);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [user]);

  const refreshCartCount = useCallback(async () => {
    let rid = null;
    try {
      rid = localStorage.getItem(LS_LAST_CART);
    } catch {
      setCartItemCount(0);
      return;
    }
    if (!rid) {
      setCartItemCount(0);
      return;
    }
    if (!user) {
      setCartItemCount(getGuestCartItemCount(rid));
      return;
    }
    try {
      const { data } = await api.get(`/api/cart?restaurantId=${encodeURIComponent(rid)}`);
      const items = Array.isArray(data?.items) ? data.items : [];
      const total = items.reduce((sum, it) => sum + Number(it.quantity || 0), 0);
      setCartItemCount(total);
    } catch {
      setCartItemCount(0);
    }
  }, [user]);

  useEffect(() => {
    refreshCartCount();
  }, [refreshCartCount, location.pathname, location.search]);

  useEffect(() => {
    const onCartChanged = () => {
      refreshCartCount();
    };
    window.addEventListener(CART_CHANGED_EVENT, onCartChanged);
    return () => window.removeEventListener(CART_CHANGED_EVENT, onCartChanged);
  }, [refreshCartCount]);

  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;
    const menuRestaurantId = getRestaurantIdFromMenuPath(location.pathname);
    if (menuRestaurantId && hasMenuScrollRestore(menuRestaurantId)) return;
    el.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(LS_SIDEBAR_EXPANDED);
      if (stored != null) setSidebarExpanded(stored !== "0");
    } catch {}
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let timer;
    const arm = () => {
      if (timer) clearTimeout(timer);
      const scheduled = readScheduledSharePrompt();
      if (!scheduled?.orderId) return;
      const wait = Math.max(0, new Date(scheduled.eligibleAt).getTime() - Date.now());
      timer = setTimeout(() => {
        clearScheduledSharePrompt();
        const path = `/share-taste/${scheduled.orderId}`;
        if (location.pathname.startsWith("/share-taste/")) return;
        const title = t("tastes.howWasItTitle");
        const platformSharePoints =
          Number(scheduled?.platformSharePoints) > 0
            ? scheduled.platformSharePoints
            : feedShareRewards?.enabled
              ? feedShareRewards.pointsPerShare
              : 0;
        const restaurantSharePoints = Number(scheduled?.restaurantSharePoints) || 0;
        const rewardBody = sharePromptRewardMessage(t, {
          platformPoints: platformSharePoints,
          restaurantPoints: restaurantSharePoints,
        });
        const body = rewardBody || t("tastes.askPhotoTitle");
        toast(title, {
          description: body,
          duration: 20000,
          action: {
            label: t("tastes.shareToFeed"),
            onClick: () => navigate(path),
          },
        });
        try {
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            const n = new Notification(title, { body, icon: "/logopwa.png" });
            n.onclick = () => {
              window.focus();
              navigate(path);
              n.close();
            };
          }
        } catch {
          /* browser may block */
        }
      }, wait);
    };
    arm();
    window.addEventListener(SHARE_PROMPT_EVENT, arm);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(SHARE_PROMPT_EVENT, arm);
    };
  }, [user, t, navigate, location.pathname, feedShareRewards]);

  const toggleSidebar = useCallback(() => {
    setSidebarExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(LS_SIDEBAR_EXPANDED, next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);

  const lastCartRestaurantId = typeof localStorage !== "undefined" ? localStorage.getItem(LS_LAST_CART) : null;

  const isSearch = location.pathname === "/explore";
  const isFavorites = location.pathname === "/favorites";
  const isFeed = location.pathname === "/feed" || location.pathname.startsWith("/t/");
  const isOrders =
    location.pathname === "/orders" ||
    location.pathname.startsWith("/orders/") ||
    location.pathname.startsWith("/order/");
  const isSettings = location.pathname === "/settings";
  const myProfilePath = user?.userRefCode ? userProfilePath(user.userRefCode) : "/login";
  const isProfile = Boolean(
    user?.userRefCode && location.pathname.toLowerCase() === userProfilePath(user.userRefCode).toLowerCase()
  );
  const isRestaurantMenuPage = /^\/restaurant\/[^/]+$/.test(location.pathname);
  const isChatThread = /^\/messages\/.+/.test(location.pathname);

  const openMyProfile = useCallback(
    async (e) => {
      if (user?.userRefCode || !user) return;
      e.preventDefault();
      try {
        const res = await api.get("/api/profile-tastes/me");
        const ref = res.data?.data?.userRefCode;
        if (ref) navigate(userProfilePath(ref));
        else toast.error(t("tastes.profileMissingBody"));
      } catch {
        toast.error(t("tastes.profileMissingBody"));
      }
    },
    [user, navigate, t]
  );

  const activeRestaurantForTable = useMemo(() => {
    const p = location.pathname;
    const rMenu = p.match(/^\/restaurant\/([^/]+)/);
    if (rMenu?.[1]) return rMenu[1];
    const rCart = p.match(/^\/cart\/([^/]+)/);
    if (rCart?.[1]) return rCart[1];
    return lastCartRestaurantId || null;
  }, [location.pathname, lastCartRestaurantId]);

  const tableStrip = useMemo(() => {
    if (!activeRestaurantForTable) return null;
    const c = getDineInTableQrContext(activeRestaurantForTable);
    if (!c?.label) return null;
    const viewOnly = c.qrOrderingEnabled === false;
    if (!viewOnly && !isDineInTableQrSeatSetupComplete(activeRestaurantForTable)) return null;
    const seats = viewOnly ? "" : (c.seatIds || []).map((s) => Number(s) + 1).join(", ");
    return { restaurantId: activeRestaurantForTable, label: c.label, seats, viewOnly };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRestaurantForTable, location.pathname, tableQrStripTick]);

  const menuBtnAria = sidebarExpanded ? t("layout.collapseMenu") : t("layout.expandMenu");
  const toggleRowClass = sidebarExpanded
    ? "mx-2 mb-1 flex items-center gap-2 rounded-xl px-2 py-1"
    : "mx-1.5 mb-1 flex justify-center rounded-xl py-2";

  const userNavLinks = (
    <>
      <NavLink
        to={myProfilePath}
        title={t("layout.profile")}
        onClick={openMyProfile}
        className={() => navLinkClass(isProfile, sidebarExpanded)}
      >
        <FiUser size={20} strokeWidth={2} className="shrink-0" />
        {sidebarExpanded ? <span>{t("layout.profile")}</span> : null}
      </NavLink>
      <NavLink
        to="/feed"
        title={t("layout.feed")}
        className={({ isActive }) => navLinkClass(isActive, sidebarExpanded)}
      >
        <FiRss size={20} strokeWidth={2} className="shrink-0" />
        {sidebarExpanded ? <span className="truncate">{t("layout.feed")}</span> : null}
      </NavLink>
      <NavLink
        to="/favorites"
        title={t("layout.favorites")}
        className={({ isActive }) => navLinkClass(isActive, sidebarExpanded)}
      >
        <FiHeart size={20} strokeWidth={2} className="shrink-0" />
        {sidebarExpanded ? <span className="truncate">{t("layout.favorites")}</span> : null}
      </NavLink>
      <NavLink
        to="/explore"
        title={t("layout.search")}
        className={({ isActive }) => navLinkClass(isActive, sidebarExpanded)}
      >
        <FiSearch size={20} strokeWidth={2} className="shrink-0" />
        {sidebarExpanded ? <span>{t("layout.search")}</span> : null}
      </NavLink>
      <NavLink
        to="/orders"
        title={t("layout.orders")}
        className={({ isActive }) => navLinkClass(isActive, sidebarExpanded)}
      >
        <FiShoppingBag size={20} strokeWidth={2} className="shrink-0" />
        {sidebarExpanded ? <span>{t("layout.orders")}</span> : null}
      </NavLink>
      {user ? (
        <NavLink
          to="/messages"
          title={t("layout.messages")}
          className={({ isActive }) =>
            navLinkClass(isActive || location.pathname.startsWith("/messages"), sidebarExpanded)
          }
        >
          <span className="relative inline-flex shrink-0">
            <FiMessageCircle size={20} strokeWidth={2} />
            {!sidebarExpanded && chatUnreadCount > 0 ? (
              <span className="absolute -top-1.5 -right-2 min-w-[15px] h-[15px] px-0.5 flex items-center justify-center rounded-full bg-brand text-white text-[8px] font-bold leading-none">
                {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
              </span>
            ) : null}
          </span>
          {sidebarExpanded ? <span className="truncate">{t("layout.messages")}</span> : null}
          {sidebarExpanded && chatUnreadCount > 0 ? (
            <span className="ml-auto min-w-[1.25rem] rounded-full bg-brand px-1.5 py-0.5 text-center text-[10px] font-bold leading-none text-white">
              {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
            </span>
          ) : null}
        </NavLink>
      ) : null}
      {lastCartRestaurantId && cartItemCount > 0 ? (
        <NavLink
          to={`/cart/${lastCartRestaurantId}`}
          title={t("layout.cart")}
          className={({ isActive }) => navLinkClass(isActive, sidebarExpanded)}
        >
          <span className={`relative inline-flex shrink-0 ${sidebarExpanded ? "" : ""}`}>
            <FiShoppingCart size={20} strokeWidth={2} />
            {!sidebarExpanded && cartItemCount > 0 ? (
              <span className="absolute -top-1.5 -right-2 min-w-[15px] h-[15px] px-0.5 flex items-center justify-center rounded-full bg-brand text-white text-[8px] font-bold leading-none">
                {cartItemCount > 99 ? "99+" : cartItemCount}
              </span>
            ) : null}
          </span>
          {sidebarExpanded ? <span className="truncate">{t("layout.cart")}</span> : null}
          {sidebarExpanded && cartItemCount > 0 ? (
            <span className="ml-auto min-w-[1.25rem] rounded-full bg-brand px-1.5 py-0.5 text-center text-[10px] font-bold leading-none text-white">
              {cartItemCount > 99 ? "99+" : cartItemCount}
            </span>
          ) : null}
        </NavLink>
      ) : null}
      {user ? (
        <NavLink
          to="/notifications"
          title={t("layout.notifications")}
          className={({ isActive }) => navLinkClass(isActive, sidebarExpanded)}
        >
          <span className="relative inline-flex shrink-0">
            <FiBell size={20} strokeWidth={2} />
            {!sidebarExpanded && bellUnreadCount > 0 ? (
              <span className="absolute -top-1.5 -right-2 min-w-[15px] h-[15px] px-0.5 flex items-center justify-center rounded-full bg-brand text-white text-[8px] font-bold leading-none">
                {bellUnreadCount > 99 ? "99+" : bellUnreadCount}
              </span>
            ) : null}
          </span>
          {sidebarExpanded ? <span className="truncate">{t("layout.notifications")}</span> : null}
          {sidebarExpanded && bellUnreadCount > 0 ? (
            <span className="ml-auto min-w-[1.25rem] rounded-full bg-brand px-1.5 py-0.5 text-center text-[10px] font-bold leading-none text-white">
              {bellUnreadCount > 99 ? "99+" : bellUnreadCount}
            </span>
          ) : null}
        </NavLink>
      ) : null}
      <NavLink
        to="/settings"
        title={t("layout.settings")}
        className={({ isActive }) => navLinkClass(isActive, sidebarExpanded)}
      >
        <FiSettings size={20} strokeWidth={2} className="shrink-0" />
        {sidebarExpanded ? <span>{t("layout.settings")}</span> : null}
      </NavLink>
    </>
  );

  return (
    <div className={`flex min-h-screen h-[100dvh] lg:h-screen flex-col bg-[#f3f6f4] font-sans overflow-hidden ${isChatThread ? "pb-0" : "pb-[calc(4.25rem+env(safe-area-inset-bottom))]"} lg:pb-0`}>
      <PhoneNumberPromptModal />
      <StudentProfilePromptModal />

      <header className={`fixed top-0 left-0 right-0 z-[9999] box-border h-[var(--user-header-offset)] min-h-[var(--user-header-offset)] shrink-0 items-center justify-between gap-3 border-b border-slate-200/90 bg-white/90 px-4 pt-[env(safe-area-inset-top,0px)] backdrop-blur-md sm:px-6 ${isChatThread ? "hidden lg:flex" : "flex"}`}>
        <SiteBrandMark
          size="md"
          linkTo="/explore"
          nameOverride={siteName}
          tagline={t("layout.tagline")}
          className="min-w-0"
          nameClassName="block text-sm sm:text-lg font-bold text-slate-900 leading-tight"
          taglineClassName="block text-[10px] sm:text-xs text-slate-500 font-medium leading-tight"
        />
        <div className="flex items-center gap-1 shrink-0">
          <NotificationBell role="user" enabled={!!user} unreadCount={bellUnreadCount} />
          {user ? (
            <NavLink
              to="/messages"
              title={
                chatUnreadCount > 0
                  ? `${t("layout.messages")} (${chatUnreadCount > 99 ? "99+" : chatUnreadCount})`
                  : t("layout.messages")
              }
              aria-label={
                chatUnreadCount > 0
                  ? `${t("layout.messages")} (${chatUnreadCount > 99 ? "99+" : chatUnreadCount})`
                  : t("layout.messages")
              }
              className={({ isActive }) =>
                `relative inline-flex items-center justify-center rounded-lg p-2 ${
                  isActive || location.pathname.startsWith("/messages")
                    ? "bg-brand/15 text-brand"
                    : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <FiMessageCircle size={20} strokeWidth={2} />
              {chatUnreadCount > 0 ? (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-0.5 flex items-center justify-center rounded-full bg-brand text-white text-[9px] font-bold leading-none">
                  {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
                </span>
              ) : null}
            </NavLink>
          ) : null}
          <LanguageSelector className="shrink-0" />
        </div>
      </header>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside
          className={`hidden lg:flex flex-col shrink-0 border-r border-slate-200/90 bg-white pt-[var(--user-header-offset)] transition-[width] duration-300 ease-out overflow-hidden ${
            sidebarExpanded ? "w-[17rem]" : "w-16"
          }`}
        >
          <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-1 pb-4" aria-label={t("layout.mainMenu")}>
            <div className={toggleRowClass}>
              <button
                type="button"
                className={sidebarMenuBtnClass}
                onClick={toggleSidebar}
                aria-label={menuBtnAria}
                aria-expanded={sidebarExpanded}
                title={menuBtnAria}
              >
                <FiMenu size={22} className="shrink-0" />
              </button>
              {sidebarExpanded ? (
                <span className="min-w-0 truncate text-sm font-bold text-slate-800">{t("layout.mainMenu")}</span>
              ) : null}
            </div>
            {userNavLinks}
          </nav>
        </aside>

        <div
          className={`flex flex-1 flex-col min-w-0 min-h-0 overflow-hidden ${
            isChatThread
              ? "max-lg:pt-[env(safe-area-inset-top,0px)] lg:pt-[var(--user-header-offset)]"
              : tableStrip
                ? "pt-[var(--user-main-offset-with-table-strip)]"
                : "pt-[var(--user-header-offset)]"
          }`}
        >
        {tableStrip && (
          <div className="fixed top-[var(--user-header-offset)] left-0 right-0 z-[9998] shrink-0 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 px-4 py-2 bg-amber-50/95 border-b border-amber-200/80 text-xs text-amber-950 backdrop-blur-sm">
            <span className="font-semibold text-center sm:text-left">
              {tableStrip.viewOnly
                ? t("layout.tableQrStripBadgeViewOnly", { table: tableStrip.label })
                : t("layout.tableQrStripBadge", { table: tableStrip.label, seats: tableStrip.seats })}
            </span>
            <Link
              to={buildRestaurantMenuUrlWithTableQr(tableStrip.restaurantId)}
              className="font-semibold text-brand underline underline-offset-2"
            >
              {tableStrip.viewOnly ? t("layout.tableQrStripMenuViewOnly") : t("layout.tableQrStripMenu")}
            </Link>
            {!tableStrip.viewOnly && (
              <Link
                to={buildDiningTableSessionUrl(tableStrip.restaurantId)}
                className="font-semibold text-brand underline underline-offset-2"
              >
                {t("layout.tableQrStripBill")}
              </Link>
            )}
          </div>
        )}

        <main
          id="app-main-scroll"
          ref={mainScrollRef}
          className={`min-h-0 flex-1 user-shell ${isChatThread ? "flex flex-col overflow-hidden" : "overflow-auto"}`}
        >
          {isChatThread ? null : <StudentStatusBanner />}
          <div
            className={
              isChatThread
                ? "mx-auto flex h-full min-h-0 w-full max-w-3xl flex-col"
                : `mx-auto w-full max-w-6xl px-4 pt-2 sm:px-6 pb-5 sm:pb-7 lg:pt-0 ${
                    isRestaurantMenuPage ? "lg:pb-4" : ""
                  }`
            }
          >
            <Suspense fallback={<div className="py-4"><PageSkeleton /></div>}>
              <Outlet />
            </Suspense>
          </div>
        </main>
        {!isFeed && !isChatThread ? (
          <div className="hidden lg:block">
            <AppFooter variant="compact" />
          </div>
        ) : null}
        </div>
      </div>

      <nav
        className={`${isChatThread ? "hidden" : "flex"} fixed bottom-0 left-0 right-0 z-50 items-stretch h-[4.25rem] bg-white/95 backdrop-blur-lg border-t border-slate-200/90 shadow-[0_-1px_12px_rgba(15,23,42,0.06)] lg:hidden pb-[env(safe-area-inset-bottom)]`}
        aria-label={t("layout.search")}
      >
        <NavLink
          to={myProfilePath}
          aria-label={t("layout.profile")}
          onClick={openMyProfile}
          className={mobileTabClass(isProfile)}
        >
          <FiUser size={21} strokeWidth={isProfile ? 2.5 : 1.75} />
          <span className="sr-only">{t("layout.profile")}</span>
        </NavLink>
        <NavLink to="/feed" aria-label={t("layout.feed")} className={mobileTabClass(isFeed)}>
          <FiRss size={21} strokeWidth={isFeed ? 2.5 : 1.75} />
          <span className="sr-only">{t("layout.feed")}</span>
        </NavLink>
        <NavLink to="/favorites" aria-label={t("layout.favorites")} className={mobileTabClass(isFavorites)}>
          <FiHeart size={21} strokeWidth={isFavorites ? 2.5 : 1.75} />
          <span className="sr-only">{t("layout.favorites")}</span>
        </NavLink>

        <NavLink
          to="/explore"
          aria-label={t("layout.search")}
          onClick={(e) => {
            if (isSearch) e.preventDefault();
          }}
          className={mobileTabClass(isSearch)}
        >
          <span
            className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
              isSearch ? "bg-brand text-white shadow-sm" : "bg-slate-100 text-slate-600"
            }`}
          >
            <FiSearch size={22} strokeWidth={2} />
          </span>
          <span className="sr-only">{t("layout.search")}</span>
        </NavLink>

        <NavLink to="/orders" aria-label={t("layout.orders")} className={mobileTabClass(isOrders)}>
          <FiShoppingBag size={21} strokeWidth={isOrders ? 2.5 : 1.75} />
          <span className="sr-only">{t("layout.orders")}</span>
        </NavLink>

        {lastCartRestaurantId && cartItemCount > 0 ? (
          <NavLink
            to={`/cart/${lastCartRestaurantId}`}
            aria-label={t("layout.cart")}
            className={({ isActive }) => mobileTabClass(isActive)}
          >
            <span className="relative inline-flex">
              <FiShoppingCart size={22} strokeWidth={1.75} />
              <span className="absolute -top-1 -right-2 min-w-[17px] h-[17px] px-0.5 flex items-center justify-center rounded-full bg-brand text-white text-[9px] font-bold leading-none">
                {cartItemCount > 99 ? "99+" : cartItemCount}
              </span>
            </span>
            <span className="sr-only">{t("layout.cart")}</span>
          </NavLink>
        ) : null}

        <NavLink to="/settings" aria-label={t("layout.settings")} className={mobileTabClass(isSettings)}>
          <FiSettings size={21} strokeWidth={isSettings ? 2.5 : 1.75} />
          <span className="sr-only">{t("layout.settings")}</span>
        </NavLink>
      </nav>
    </div>
  );
};

export default UserLayout;
